import { Modal } from 'bootstrap';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import uuid from 'react-uuid';
import api from '../../services/api';
import { INVENTORY_API_BASE_URL_2 } from '../../services/TapApiUrls';
import TapIcon from '../../services/TapIcon';
import Loader from '../../components/ui/Loader/Loader';
import DateService from '../../services/DateService';

const ItemQrCodePrintModal = forwardRef((props, ref) => {

    const accessToken = useSelector(state => state.auth.access_token);
    const currentData = DateService.dateTimeFormat(new Date(), 'DD-MM-YYYY')

    const [loading, setLoading] = useState(false);
    const [printData, setPrintData] = useState({});
    
    const [searchModalId, setSearchModalId] = useState(uuid());
    const searchModalElem = useRef(null);
    const searchModal = useRef(null);
    const searchModalOpen = () => searchModal.current && searchModal.current.show()
    const searchModalClose = () => searchModal.current && searchModal.current.hide()
    
    useEffect(() => {
        searchModalElem.current = document.getElementById(searchModalId);
        searchModal.current = new Modal(searchModalElem.current, { keyboard: false, backdrop: false });
        return () => {
            searchModal.current && searchModal.current.dispose();
        };
    }, []);

    useImperativeHandle(ref, () => ({
        initModalHandler: (item_id, params) => searchModalOpenHandler(item_id, params),
        initModalClose: () => editModalCloseHandler()
    }));

    const searchModalOpenHandler = (item_id, params) => {
        searchModalOpen();
        fetchQRCode(item_id, params)
    }

    const editModalCloseHandler = () => {
        searchModalClose();
    }
    
    const fetchQRCode = async (item_id, params) => {
        setLoading(true);
        try {
            const response = await api.server2Api.get(`${INVENTORY_API_BASE_URL_2}/item_qr_code/${item_id}`, { 
                headers: { 'Authorization': 'Bearer ' + accessToken }, 
                params 
            });
            setPrintData(response.data);
        } catch (error) {
            toast.error('Failed to fetch QR code', { position: toast.POSITION.TOP_RIGHT });
        } finally {
            setLoading(false);
        }
    };
    
    const decodeBase64SVG = (base64String) => {
        if (!base64String) return '';
        let base64Data = base64String.includes('base64,')
            ? base64String.split('base64,')[1]
            : base64String;
        try {
            return atob(base64Data);
        } catch (error) {
            console.error('Error decoding:', error);
            return '';
        }
    };

    const limitText = (text, maxLength) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
    };

    const printHandle = () => {
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
            toast.error('Please allow pop-ups for this site', { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        const companyName = limitText(printData?.company_data?.name, 45);
        const manufacturer = limitText(printData?.manufacturer, 30);
        const productName = limitText(printData?.name, 30);
        const productCode = limitText(printData?.code, 20);
        const qrValue = printData?.qr_code_url || printData?.code || `https://nexus.com/item/${printData?.code}`;


        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Print QR Label</title>
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        @page {
                            size: 4in 2in landscape;
                            margin: 0mm;
                        }
                        body {
                            margin: 0;
                            padding: 0;
                            background: white;
                            font-family: 'Segoe UI', 'Arial', sans-serif;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .thermal-label {
                            width: 4in;
                            height: 2in;
                            background: white;
                            border: 1px solid #e0e0e0;
                            padding: 7px 10px;
                            box-sizing: border-box;
                            display: flex;
                            flex-direction: column;
                        }
                        .label-header {
                            font-size: 14px;
                            font-weight: 700;
                            text-align: center;
                            color:#000000;
                            letter-spacing: 0.3px;
                            text-transform: uppercase;
                            margin-bottom: 6px ;
                            margin-top: 6px ;

                        }
                        
                        .label-content {
                            display: flex;
                            align-items:center;
                            justify-content:space-between;
                            gap: 10px;
                        }
                        .info-section {
                            display: flex;
                            flex-direction:column;
                        }
                        .info-row {
                            display: flex;
                            margin-bottom: 5px;
                            align-items: baseline;
                        }
                        .info-label {
                            font-size: 11px;
                            font-weight: 600;
                            color: #000000;
                            width: 70px;
                            text-transform: uppercase;
                        }
                        .info-value {
                            font-size: 11px;
                            font-weight: 600;
                            color: #000000;
                            word-break: break-word;
                        }
                        .qr-section {
                            width: 75px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            background: #fafafa;
                            border-radius: 6px;
                            padding: 4px;
                            margin-right:5px
                        }
                        .qr-wrapper img {
                            width: 65px;
                            height: 65px;
                        }
                        .qr-label-text {
                            font-size: 6px;
                            color: #95a5a6;
                            margin-top: 3px;
                            text-align: center;
                        }
                    
                        .print-date {
                            font-size: 10px;
                            color: #000000;
                            margin-top:3px 4px;
                            text-align:end
                        }
                        
                        @media print {
                            .thermal-label {
                                border: 1px solid #ddd;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="thermal-label">
                        <span class="label-header">
                            ${companyName || 'NEXUS DEVELOPMENT'}
                        </span>
                        <div class="label-content">
                            <div class="info-section">
                                <div class="info-row">
                                    <span class="info-label">NAME</span>
                                    <span class="info-value">${productName}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">CODE</span>
                                    <span class="info-value">${productCode}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">MANF.</span>
                                    <span class="info-value">${manufacturer}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">BATCH NO</span>
                                    <span class="info-value">${printData?.batch_number || '-'}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">EXPIRY DATE</span>
                                    <span class="info-value">${printData?.expiry_date && printData.expiry_date != null ? printData?.expiry_date : '-'}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">QTY</span>
                                    <span class="info-value">${printData?.qty || '-'}</span>
                                </div>
                            </div>
                            <div class="qr-section">
                                <div class="qr-wrapper">
                                    ${decodeBase64SVG(printData?.qr_code)}
                                </div>
                            </div>
                        </div>
                        <span class="print-date">PRINTED: ${currentData}</span>
                    </div>
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { 
                                    window.close(); 
                                }, 500);
                            }, 300);
                        };
                    <\/script>
                </body>
            </html>
        `);
        printWindow.document.close();
    }

    const qrCodeLabelPrinter = () => {
        return (
            <div
                style={{
                    width: '100%',
                    minHeight: '200px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        width: '3.8in',
                        height: '1.9in',
                        background: '#fff',
                        border: '2px solid #000',
                        padding: '8px',
                        boxSizing: 'border-box',
                    }}
                >
                    <div>
                        <p
                            style={{
                                margin: 0,
                                fontSize: '12px',
                                fontWeight: '700',
                                textAlign: 'center',
                                paddingBottom: '4px',
                                borderBottom: '1px solid #ddd',
                                marginBottom: '4px',
                            }}
                        >
                            {limitText(printData?.company_data?.name, 45)}
                        </p>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ fontSize: '10px', width: '70%' }}>
                            <div style={{ margin: '2px 0', display: 'flex' ,gap:"5px"  }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>NAME</span>
                                <span><b>{limitText(printData?.name, 25)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex'  ,gap:"5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>CODE</span>
                                <span><b>{limitText(printData?.code, 20)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex' ,gap:"5px"  }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>MANF.</span>
                                <span><b>{limitText(printData?.manufacturer, 25)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex'  ,gap:"5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>BATCH NO</span>
                                <span><b>{limitText(printData?.batch_number, 15)}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex'  ,gap:"5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>EXPIRY DATE</span>
                                <span><b>{printData?.expiry_date && printData.expiry_date != null ? printData?.expiry_date : '-'}</b></span>
                            </div>
                            <div style={{ margin: '2px 0', display: 'flex'  ,gap:"5px" }}>
                                <span style={{ fontWeight: 'bold', width: '70px' }}>QTY</span>
                                <span><b>{printData?.qty || '-'}</b></span>
                            </div>
                        </div>
                        <div
                            style={{
                                width: '28%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    width: '75px',
                                    height: '75px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                dangerouslySetInnerHTML={{ __html: decodeBase64SVG(printData?.qr_code) }} 
                            />
                        </div>
                    </div>

                    <p
                        style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: "0px",
                            marginTop: "4px",
                            textAlign: "right",
                            paddingTop: "3px",
                            borderTop: "1px solid #eee",
                        }}
                    >
                        PRINT DATE : {currentData}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="modal fade" id={searchModalId} tabIndex="-1" ref={searchModalElem}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Print QR Label</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row align-items-center mb-3 mt-3" style={{ minHeight: '280px' }}>
                                {loading ? <Loader /> : qrCodeLabelPrinter()}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                            <button type="button" className="btn btn-primary" onClick={printHandle}>
                                <TapIcon.FontAwesomeIcon icon={TapIcon.faPrint} className="me-1" />
                                Print Label
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default ItemQrCodePrintModal;